// Detail van één breakdown-item: status, wie het oppakt, deadline, notitie, de voorstellen
// (opties) waarvan er één gekozen kan worden, en de documenten-/contractenchecklist.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, IconButton,
  InputBase, InputLabel, Link, MenuItem, Select, TextField, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import { bevestig } from '../../utils/bevestig';
import { biedOngedaanAan } from '../../utils/ongedaan';
import { meldFout } from '../../utils/apiFout';
import {
  api, bijlageLink, bijlageToevoegen, documentSuggesties, documentTemplate, dupliceerItem, fotoToevoegen, kiesOptie,
  lijstDocumenten, nieuwDocument, nieuweOptie, perItem, veiligeLink, wijzigDocument, wijzigItem,
  ACCENT, CATEGORIE, LIJN, STATUSSEN, STATUS_VOLGORDE, STIL,
} from './breakdownApi';

const LEGE_OPTIE = { name: '', price: '', conditions: '', links: '' };
const TEKSTVELDEN = ['name', 'owner', 'assigned_to', 'due_date', 'scene_number', 'notes'];
const velden = (item) => Object.fromEntries(TEKSTVELDEN.map((k) => [k, item?.[k] ?? '']));
const MAX_BIJLAGE = 25 * 1024 * 1024;
const MAX_FOTO = 10 * 1024 * 1024;
const MAX_FOTOS = 12;

function Fotos({ item, optie, onGewijzigd }) {
  const [bezig, setBezig] = useState(0);
  const invoer = useRef(null);
  const fotos = optie.photos || [];

  async function kies(e) {
    const bestanden = Array.from(e.target.files || []);
    e.target.value = '';
    const ruimte = MAX_FOTOS - fotos.length;
    if (bestanden.length > ruimte) meldFout(new Error(`Maximaal ${MAX_FOTOS} foto's per optie — ${bestanden.length - ruimte} overgeslagen`), "Foto's uploaden");
    for (const b of bestanden.slice(0, Math.max(0, ruimte))) {
      if (b.size > MAX_FOTO) { meldFout(new Error(`${b.name} is groter dan 10 MB`), 'Foto uploaden'); continue; }
      setBezig((n) => n + 1);
      try { onGewijzigd(await fotoToevoegen(item.id, optie.id, b)); }
      catch (err) { meldFout(err, 'Foto uploaden'); }
      finally { setBezig((n) => n - 1); }
    }
  }

  async function weg(f) {
    const ok = await bevestig({ titel: 'Foto weghalen?', tekst: `${f.name || 'Deze foto'} wordt van "${optie.name}" verwijderd.`, knop: 'Weghalen', gevaarlijk: true });
    if (!ok) return;
    try {
      onGewijzigd(await perItem(item.id, () => api(`/items/${item.id}/options/${optie.id}/photos/${f.id}`, { method: 'DELETE', wat: 'Foto weghalen' })));
    } catch (e) { meldFout(e, 'Foto weghalen'); }
  }

  return (
    <Box sx={{ mt: 1, display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'center' }}>
      {fotos.map((f) => (veiligeLink(f.url) ? (
        <Box key={f.id || f.url} sx={{ position: 'relative', width: 72, height: 72, '&:hover .weg': { opacity: 1 } }}>
          <a href={veiligeLink(f.url)} target="_blank" rel="noopener noreferrer" title={f.name || 'Foto'}>
            <Box component="img" src={veiligeLink(f.url)} alt={f.name || `Foto bij ${optie.name}`} loading="lazy"
              sx={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 1.5, border: `1px solid ${LIJN}`, display: 'block' }} />
          </a>
          {f.id && (
            <IconButton className="weg" size="small" aria-label={`Foto ${f.name || ''} weghalen`} onClick={() => weg(f)}
              sx={{ position: 'absolute', top: -8, right: -8, bgcolor: '#fff', border: `1px solid ${LIJN}`, p: 0.25, opacity: { xs: 1, md: 0 }, '&:hover': { bgcolor: '#fff' } }}>
              <CloseIcon sx={{ fontSize: 14 }} />
            </IconButton>
          )}
        </Box>
      ) : null))}
      {fotos.length < MAX_FOTOS && (
        <Button size="small" startIcon={<AddPhotoAlternateOutlinedIcon />} disabled={bezig > 0} onClick={() => invoer.current?.click()}
          sx={{ textTransform: 'none', color: STIL }}>
          {bezig > 0 ? 'Uploaden…' : fotos.length ? 'Foto' : "Foto's toevoegen"}
        </Button>
      )}
      <input ref={invoer} type="file" hidden multiple accept="image/jpeg,image/png,image/webp,image/gif,image/heic,image/heif" onChange={kies} />
    </Box>
  );
}

function Sectie({ children }) {
  return (
    <Typography sx={{ mt: 1, fontSize: 13, fontWeight: 700, color: ACCENT, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
      {children}
    </Typography>
  );
}

function Documenten({ item }) {
  const [docs, setDocs] = useState(null);
  const [suggesties, setSuggesties] = useState([]);
  const [nieuw, setNieuw] = useState('');
  const [uploaden, setUploaden] = useState(null);
  const bestandRef = useRef(null);
  const doelRef = useRef(null);

  const laad = useCallback(async () => {
    try { setDocs(await lijstDocumenten(item.id)); } catch (e) { meldFout(e, 'Documenten laden'); setDocs([]); }
  }, [item.id]);
  useEffect(() => { laad(); }, [laad]);
  useEffect(() => {
    let weg = false;
    documentSuggesties(item.category).then((s) => { if (!weg) setSuggesties(s); }).catch(() => {});
    return () => { weg = true; };
  }, [item.category]);

  const vervang = (d) => setDocs((lijst) => (lijst || []).map((x) => (x.id === d.id ? d : x)));

  async function voegToe(naam, bewaarAlsTemplate) {
    const n = naam.trim();
    if (!n) return;
    try {
      const d = await nieuwDocument(item.id, n);
      setDocs((lijst) => [...(lijst || []), d]);
      setNieuw('');
      if (bewaarAlsTemplate && !suggesties.some((s) => s.toLowerCase() === n.toLowerCase())) {
        documentTemplate(item.category, n).then(() => setSuggesties((s) => [...s, n])).catch(() => {});
      }
    } catch (e) { meldFout(e, 'Document toevoegen'); }
  }

  async function vink(d) {
    const vorige = d.is_done;
    try {
      vervang(await wijzigDocument(item.id, d.id, { is_done: !vorige }));
      biedOngedaanAan(`"${d.name}" ${vorige ? 'weer open' : 'afgevinkt'}`,
        async () => vervang(await wijzigDocument(item.id, d.id, { is_done: vorige })));
    } catch (e) { meldFout(e, 'Document opslaan'); }
  }

  async function verwijder(d) {
    const ok = await bevestig({ titel: 'Document verwijderen?', tekst: `"${d.name}"${d.has_file ? ' en de bijlage' : ''} wordt van dit item verwijderd.`, knop: 'Verwijderen', gevaarlijk: true });
    if (!ok) return;
    try {
      await api(`/items/${item.id}/documents/${d.id}`, { method: 'DELETE', wat: 'Document verwijderen' });
      setDocs((lijst) => (lijst || []).filter((x) => x.id !== d.id));
    } catch (e) { meldFout(e, 'Document verwijderen'); }
  }

  async function bijlageWeg(d) {
    const ok = await bevestig({ titel: 'Bijlage weghalen?', tekst: `De bijlage "${d.file_name}" wordt losgekoppeld van "${d.name}".`, knop: 'Weghalen', gevaarlijk: true });
    if (!ok) return;
    try { vervang(await api(`/items/${item.id}/documents/${d.id}/file`, { method: 'DELETE', wat: 'Bijlage weghalen' })); }
    catch (e) { meldFout(e, 'Bijlage weghalen'); }
  }

  async function open(d) {
    // Venster vóór de await openen: anders blokkeert de browser het als pop-up.
    const venster = window.open('', '_blank');
    if (venster) venster.opener = null;
    try {
      const { url } = await bijlageLink(item.id, d.id);
      if (venster) venster.location.href = url; else window.location.assign(url);
    } catch (e) { if (venster) venster.close(); meldFout(e, 'Bijlage openen'); }
  }

  async function gekozen(e) {
    const bestand = e.target.files?.[0];
    e.target.value = '';
    const d = doelRef.current;
    if (!bestand || !d) return;
    if (bestand.size > MAX_BIJLAGE) { meldFout(new Error('Bijlage is groter dan 25 MB'), 'Bijlage uploaden'); return; }
    setUploaden(d.id);
    try { vervang(await bijlageToevoegen(item.id, d.id, bestand)); }
    catch (err) { meldFout(err, 'Bijlage uploaden'); }
    finally { setUploaden(null); }
  }

  const open_ = (docs || []).filter((d) => !d.is_done).length;
  const nogNiet = suggesties.filter((s) => !(docs || []).some((d) => d.name.toLowerCase() === s.toLowerCase()));

  return (
    <>
      <Sectie>Documenten / contracten {docs ? `(${docs.length - open_}/${docs.length})` : ''}</Sectie>
      {(docs || []).map((d) => (
        <Box key={d.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, border: `1px solid ${LIJN}`, borderRadius: 1.5, pl: 0.5, pr: 1 }}>
          <Checkbox size="small" checked={d.is_done} onChange={() => vink(d)} inputProps={{ 'aria-label': `${d.name} afvinken` }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 14, textDecoration: d.is_done ? 'line-through' : 'none', color: d.is_done ? STIL : 'inherit' }}>{d.name}</Typography>
            {d.has_file && (
              <Link component="button" type="button" onClick={() => open(d)} underline="hover" sx={{ fontSize: 12.5 }}>
                📎 {d.file_name || 'bijlage'}
              </Link>
            )}
          </Box>
          {d.has_file ? (
            <Button size="small" onClick={() => bijlageWeg(d)} sx={{ textTransform: 'none', color: STIL, minWidth: 0 }}>Bijlage weg</Button>
          ) : (
            <IconButton size="small" disabled={uploaden === d.id} aria-label={`Bijlage toevoegen aan ${d.name}`}
              onClick={() => { doelRef.current = d; bestandRef.current?.click(); }}>
              <AttachFileIcon fontSize="small" />
            </IconButton>
          )}
          <IconButton size="small" aria-label={`${d.name} verwijderen`} onClick={() => verwijder(d)}><DeleteOutlineIcon fontSize="small" /></IconButton>
        </Box>
      ))}
      {nogNiet.length > 0 && (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          {nogNiet.map((s) => <Chip key={s} size="small" variant="outlined" label={`+ ${s}`} onClick={() => voegToe(s, false)} />)}
        </Box>
      )}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, border: `1px dashed ${LIJN}`, borderRadius: 1.5, px: 1.25, py: 0.25 }}>
        <InputBase fullWidth value={nieuw} onChange={(e) => setNieuw(e.target.value)} placeholder="Eigen document of contract… (Enter)"
          onKeyDown={(e) => { if (e.key === 'Enter') voegToe(nieuw, true); }} sx={{ fontSize: 13.5 }} />
      </Box>
      <input ref={bestandRef} type="file" hidden accept="application/pdf,image/*,.doc,.docx" onChange={gekozen} />
    </>
  );
}

export default function ItemDialoog({ item, onClose, onGewijzigd, onVerwijderd, onToegevoegd }) {
  const [form, setForm] = useState(() => velden(item));
  const [nieuweOptieOpen, setNieuweOptieOpen] = useState(false);
  const [optieForm, setOptieForm] = useState(LEGE_OPTIE);
  const [bezig, setBezig] = useState(false);
  // Laatst verstuurde waarde per veld (met volgnummer): een fout zet het veld alleen terug als
  // er intussen niets nieuwers verstuurd of getypt is.
  const verstuurd = useRef({});
  const volgnr = useRef(0);
  const itemNu = useRef(item);
  itemNu.current = item;

  useEffect(() => { setForm(velden(item)); verstuurd.current = {}; }, [item?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!item) return null;
  const categorie = CATEGORIE[item.category];
  const opties = item.options || [];

  async function bewaarVeld(veld) {
    const waarde = form[veld];
    const laatst = veld in verstuurd.current ? verstuurd.current[veld].waarde : item[veld];
    if (String(waarde ?? '') === String(laatst ?? '')) return;
    if (veld === 'name' && !String(waarde).trim()) { setForm((f) => ({ ...f, name: laatst ?? item.name })); return; }
    const nr = ++volgnr.current;
    verstuurd.current[veld] = { waarde, nr };
    try {
      onGewijzigd(await wijzigItem(item.id, { [veld]: waarde === '' ? null : waarde }));
    } catch (e) {
      meldFout(e, 'Opslaan');
      if (verstuurd.current[veld]?.nr !== nr) return;
      delete verstuurd.current[veld];
      setForm((f) => (String(f[veld] ?? '') === String(waarde ?? '') ? { ...f, [veld]: itemNu.current?.[veld] ?? '' } : f));
    }
  }

  async function zetStatus(status) {
    const vorige = item.status;
    if (status === vorige) return;
    try {
      onGewijzigd(await wijzigItem(item.id, { status }));
      biedOngedaanAan(`Status: ${STATUSSEN[status].label}`, async () => onGewijzigd(await wijzigItem(item.id, { status: vorige })));
    } catch (e) { meldFout(e, 'Status opslaan'); }
  }

  async function kies(optie) {
    const vorige = item.selected_option_id;
    const nieuw = vorige === optie.id ? null : optie.id;
    try {
      onGewijzigd(await kiesOptie(item.id, nieuw));
      biedOngedaanAan(nieuw ? `"${optie.name}" gekozen` : 'Keuze ongedaan', async () => onGewijzigd(await kiesOptie(item.id, vorige)));
    } catch (e) { meldFout(e, 'Optie kiezen'); }
  }

  async function verwijderOptie(optie) {
    const ok = await bevestig({ titel: 'Optie verwijderen?', tekst: `"${optie.name}" wordt definitief verwijderd.`, knop: 'Verwijderen', gevaarlijk: true });
    if (!ok) return;
    try {
      onGewijzigd(await perItem(item.id, () => api(`/items/${item.id}/options/${optie.id}`, { method: 'DELETE', wat: 'Optie verwijderen' })));
    } catch (e) { meldFout(e, 'Optie verwijderen'); }
  }

  async function voegOptieToe() {
    if (!optieForm.name.trim() || bezig) return;
    setBezig(true);
    try {
      const links = optieForm.links.split('\n').map((l) => l.trim()).filter(Boolean);
      onGewijzigd(await nieuweOptie(item.id, { ...optieForm, links }));
      setOptieForm(LEGE_OPTIE);
      setNieuweOptieOpen(false);
    } catch (e) { meldFout(e, 'Optie toevoegen'); }
    finally { setBezig(false); }
  }

  async function dupliceer() {
    try {
      const kopie = await dupliceerItem(item.id);
      onToegevoegd?.(kopie);
      biedOngedaanAan(`"${kopie.name}" gemaakt`, async () => {
        await api(`/items/${kopie.id}`, { method: 'DELETE', wat: 'Kopie verwijderen' });
        onVerwijderd(kopie.id, kopie.production_id);
      });
    } catch (e) { meldFout(e, 'Dupliceren'); }
  }

  async function verwijderItem() {
    const ok = await bevestig({
      titel: 'Item verwijderen?',
      tekst: `"${item.name}"${opties.length ? `, ${opties.length} optie(s)` : ''} en de documenten worden definitief verwijderd.`,
      knop: 'Verwijderen', gevaarlijk: true,
    });
    if (!ok) return;
    try {
      await perItem(item.id, () => api(`/items/${item.id}`, { method: 'DELETE', wat: 'Item verwijderen' }));
      onVerwijderd(item.id, item.production_id);
    } catch (e) { meldFout(e, 'Item verwijderen'); }
  }

  const veld = (naam, label, extra = {}) => (
    <TextField label={label} size="small" fullWidth value={form[naam] ?? ''}
      onChange={(e) => setForm((f) => ({ ...f, [naam]: e.target.value }))}
      onBlur={() => bewaarVeld(naam)} {...extra} />
  );

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
        <Typography component="span" sx={{ fontSize: 13, color: STIL, fontWeight: 600 }}>{categorie?.icoon} {categorie?.label}</Typography>
        <Box sx={{ flex: 1 }} />
        <IconButton size="small" onClick={onClose} aria-label="Sluiten"><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.75, pt: '8px !important' }}>
        {veld('name', 'Naam')}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
          <FormControl size="small">
            <InputLabel id="pb-status">Status</InputLabel>
            <Select labelId="pb-status" label="Status" value={item.status} onChange={(e) => zetStatus(e.target.value)}>
              {STATUS_VOLGORDE.map((s) => <MenuItem key={s} value={s}>{STATUSSEN[s].label}</MenuItem>)}
            </Select>
          </FormControl>
          {veld('scene_number', 'Scène', { type: 'number', inputProps: { min: 0 } })}
          {veld('owner', 'Wie pakt het op')}
          {veld('assigned_to', 'Toegezegd door')}
          {veld('due_date', 'Deadline', { type: 'date', InputLabelProps: { shrink: true } })}
        </Box>
        {veld('notes', 'Notitie', { multiline: true, minRows: 2 })}

        <Sectie>Opties ({opties.length})</Sectie>
        {opties.length === 0 && !nieuweOptieOpen && (
          <Typography sx={{ fontSize: 13, color: STIL }}>Nog geen voorstellen. Voeg opties toe om ze te vergelijken en er één te kiezen.</Typography>
        )}
        {opties.map((o) => {
          const gekozen = item.selected_option_id === o.id;
          return (
            <Box key={o.id} sx={{ border: `1px solid ${gekozen ? '#15803d' : LIJN}`, borderRadius: 2, p: 1.5, bgcolor: gekozen ? '#f0fdf4' : '#fff' }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 600, fontSize: 14.5 }}>{o.name}</Typography>
                  {o.price && <Typography sx={{ fontSize: 13, color: ACCENT, fontWeight: 600 }}>{o.price}</Typography>}
                </Box>
                {gekozen && <Chip size="small" icon={<CheckCircleIcon />} label="Gekozen"
                  sx={{ bgcolor: '#dcfce7', color: '#15803d', '& .MuiChip-icon': { color: '#15803d' } }} />}
              </Box>
              {o.conditions && <Typography sx={{ mt: 0.75, fontSize: 13, color: '#475569', whiteSpace: 'pre-wrap' }}>{o.conditions}</Typography>}
              {o.links.length > 0 && (
                <Box sx={{ mt: 0.75, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                  {o.links.map((l) => (veiligeLink(l)
                    ? <Link key={l} href={veiligeLink(l)} target="_blank" rel="noopener noreferrer" underline="hover" noWrap sx={{ fontSize: 13 }}>{l}</Link>
                    : <Typography key={l} noWrap sx={{ fontSize: 13, color: STIL }}>{l}</Typography>))}
                </Box>
              )}
              <Fotos item={item} optie={o} onGewijzigd={onGewijzigd} />
              <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                <Button size="small" variant={gekozen ? 'outlined' : 'contained'} disableElevation onClick={() => kies(o)}
                  sx={{ textTransform: 'none', ...(gekozen ? {} : { bgcolor: ACCENT, '&:hover': { bgcolor: '#434f62' } }) }}>
                  {gekozen ? 'Keuze ongedaan maken' : 'Kies deze optie'}
                </Button>
                <Button size="small" startIcon={<DeleteOutlineIcon />} onClick={() => verwijderOptie(o)} sx={{ textTransform: 'none', color: STIL }}>Verwijderen</Button>
              </Box>
            </Box>
          );
        })}
        {nieuweOptieOpen ? (
          <Box sx={{ border: `1px dashed ${LIJN}`, borderRadius: 2, p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            <TextField label="Naam" size="small" autoFocus value={optieForm.name} onChange={(e) => setOptieForm((f) => ({ ...f, name: e.target.value }))} />
            <TextField label="Prijs" size="small" placeholder="bv. € 850 / dag" value={optieForm.price} onChange={(e) => setOptieForm((f) => ({ ...f, price: e.target.value }))} />
            <TextField label="Voorwaarden / aandachtspunten" size="small" multiline minRows={2} value={optieForm.conditions}
              onChange={(e) => setOptieForm((f) => ({ ...f, conditions: e.target.value }))} />
            <TextField label="Links (één per regel)" size="small" multiline minRows={2} value={optieForm.links}
              onChange={(e) => setOptieForm((f) => ({ ...f, links: e.target.value }))} />
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button size="small" onClick={() => { setNieuweOptieOpen(false); setOptieForm(LEGE_OPTIE); }} sx={{ textTransform: 'none', color: STIL }}>Annuleren</Button>
              <Button size="small" variant="contained" disableElevation disabled={!optieForm.name.trim() || bezig} onClick={voegOptieToe}
                sx={{ textTransform: 'none', bgcolor: ACCENT, '&:hover': { bgcolor: '#434f62' } }}>Optie opslaan</Button>
            </Box>
          </Box>
        ) : (
          <Button variant="outlined" size="small" onClick={() => setNieuweOptieOpen(true)}
            sx={{ alignSelf: 'flex-start', textTransform: 'none', color: ACCENT, borderColor: LIJN }}>+ Optie toevoegen</Button>
        )}

        <Documenten item={item} />
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button size="small" startIcon={<DeleteOutlineIcon />} onClick={verwijderItem} sx={{ textTransform: 'none', color: '#b91c1c' }}>Item verwijderen</Button>
          <Button size="small" startIcon={<ContentCopyIcon />} onClick={dupliceer} sx={{ textTransform: 'none', color: STIL }}>Dupliceren</Button>
        </Box>
        <Button size="small" onClick={onClose} sx={{ textTransform: 'none' }}>Sluiten</Button>
      </DialogActions>
    </Dialog>
  );
}
